import { logError } from '../shared/utils/logger';
import { BASE_URL, fetchWithAuth } from './client';

/**
 * Claw9 Backups API
 * Create and restore workspace backups
 */

/**
 * Create backup
 * POST /backups
 */
export async function createBackup(params) {
    const response = await fetchWithAuth(`${BASE_URL}/backups`, {
        method: 'POST',
        body: JSON.stringify({
            bot_id: params.botId || params.bot_id,
            metadata: params.metadata,
            sync_to_s3: params.syncToS3 ?? false
        })
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logError('Create backup failed:', response.status, errorData);
        throw new Error(errorData.error?.message || 'Failed to create backup');
    }

    return await response.json();
}

/**
 * List backups
 * GET /backups?bot_id=...&limit=50
 */
export async function listBackups(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.botId || params.bot_id) {
        queryParams.append('bot_id', params.botId || params.bot_id);
    }
    if (params.limit) {
        queryParams.append('limit', params.limit);
    }

    const queryString = queryParams.toString();
    const url = `${BASE_URL}/backups${queryString ? `?${queryString}` : ''}`;

    const response = await fetchWithAuth(url);

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logError('List backups failed:', response.status, errorData);
        throw new Error(errorData.error?.message || 'Failed to list backups');
    }

    return await response.json();
}

/**
 * Restore backup
 * POST /backups/{backup_id}/restore
 */
export async function restoreBackup(backupId, options = {}) {
    const response = await fetchWithAuth(`${BASE_URL}/backups/${encodeURIComponent(backupId)}/restore`, {
        method: 'POST',
        body: JSON.stringify({
            overwrite: options.overwrite ?? true
        })
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logError('Restore backup failed:', response.status, errorData);
        throw new Error(errorData.error?.message || 'Failed to restore backup');
    }

    return await response.json();
}

/**
 * Delete backup
 * DELETE /backups/{backup_id}
 */
export async function deleteBackup(backupId) {
    const response = await fetchWithAuth(`${BASE_URL}/backups/${encodeURIComponent(backupId)}`, {
        method: 'DELETE'
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logError('Delete backup failed:', response.status, errorData);
        throw new Error(errorData.error?.message || 'Failed to delete backup');
    }

    return await response.json();
}

/**
 * Download backup
 * GET /backups/{backup_id}/download
 * Returns binary .tar.gz file
 */
export async function downloadBackup(backupId) {
    const response = await fetchWithAuth(`${BASE_URL}/backups/${encodeURIComponent(backupId)}/download`);

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logError('Download backup failed:', response.status, errorData);
        throw new Error(errorData.error?.message || 'Failed to download backup');
    }

    // Return blob for download
    return await response.blob();
}
